import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { WithdrawalsService, WithdrawalContext } from '../services/withdrawals.service';
import { SupportService } from '../services/support.service';
import styles from './WithdrawalsPage.module.css';
import { useAuth } from '../context/AuthContext';

export default function WithdrawalsPage() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const { user } = useAuth();

  if (user?.role && user.role !== 'client') {
    const redirect =
      user.role === 'developer'
        ? '/developer/panel'
        : user.role === 'admin' || user.role === 'manager'
          ? '/admin/dashboard'
          : '/dashboard';
    return <Navigate to={redirect} replace />;
  }

  const [context, setContext] = useState<WithdrawalContext | null>(null);
  const [requests, setRequests] = useState<Array<{
    id: string | number;
    amount: string | number | { s?: number; e?: number; d?: number[] };
    currency: string;
    status: string;
    requested_at: string;
    approved_at?: string | null;
    executed_at?: string | null;
    reference?: string | null;
    notes?: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'EXECUTED' | 'REJECTED'>('ALL');
  const [reporting, setReporting] = useState<null | {
    id: string | number;
    amount: any;
    currency: string;
    executed_at?: string | null;
    reference?: string | null;
    notes?: string | null;
  }>(null);
  const [reportNote, setReportNote] = useState('');
  const [reportingSubmit, setReportingSubmit] = useState(false);

  const loadContext = async () => {
    setLoading(true);
    try {
      const res = await WithdrawalsService.getContext();
      setContext(res);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await WithdrawalsService.list();
      setRequests(Array.isArray(res) ? res : []);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    loadContext();
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = useMemo(() => {
    const value = context?.wallet?.available ?? '0';
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [context]);

  const amountValue = useMemo(() => {
    const parsed = Number(amount.replace(/,/g, '.'));
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [amount]);

  const bankAccount = context?.bankAccount && 'hasBankAccount' in context.bankAccount
    ? context.bankAccount
    : null;
  const isVerified = bankAccount?.hasBankAccount ? bankAccount.status === 'VERIFIED' : false;

  const canSubmit =
    !submitting &&
    !!bankAccount?.hasBankAccount &&
    isVerified &&
    amountValue > 0 &&
    amountValue <= available &&
    mfaCode.trim().length >= 6;

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      PENDING: t('withdrawals.status_pending') || 'Pendiente',
      APPROVED: t('withdrawals.status_approved') || 'Aprobado',
      EXECUTED: t('withdrawals.status_executed') || 'Ejecutado',
      REJECTED: t('withdrawals.status_rejected') || 'Rechazado'
    };
    return map[status] || status;
  };

  const statusClass = (status: string) => {
    const map: Record<string, string> = {
      PENDING: styles.badgePending,
      APPROVED: styles.badgeApproved,
      EXECUTED: styles.badgeExecuted,
      REJECTED: styles.badgeRejected
    };
    return map[status] || '';
  };

  const canReport = (req: { status: string; executed_at?: string | null }) => {
    if (req.status !== 'EXECUTED' || !req.executed_at) return false;
    const executedAt = new Date(req.executed_at).getTime();
    if (!Number.isFinite(executedAt)) return false;
    const hours = (Date.now() - executedAt) / (1000 * 60 * 60);
    return hours >= 24;
  };

  const reportWaitMessage = (req: { status: string; executed_at?: string | null }) => {
    if (req.status !== 'EXECUTED' || !req.executed_at) {
      return t('withdrawals.report_unavailable') || 'Solo disponible para retiros ejecutados.';
    }
    const executedAt = new Date(req.executed_at).getTime();
    if (!Number.isFinite(executedAt)) {
      return t('withdrawals.report_unavailable') || 'Solo disponible para retiros ejecutados.';
    }
    const elapsed = Date.now() - executedAt;
    if (elapsed >= 24 * 60 * 60 * 1000) {
      return t('withdrawals.report_ready') || 'Disponible para reportar.';
    }
    const remainingMs = 24 * 60 * 60 * 1000 - elapsed;
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.max(0, Math.ceil((remainingMs % (1000 * 60 * 60)) / (1000 * 60)));
    return (
      t('withdrawals.report_wait', { hours, minutes }) ||
      `Disponible en ${hours}h ${minutes}m`
    );
  };

  const parseAmount = (value: any) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === 'object' && Array.isArray(value.d)) {
      const digits = value.d.join('');
      const sign = value.s === -1 ? -1 : 1;
      const exponent = typeof value.e === 'number' ? value.e : digits.length - 1;
      const decimal = exponent - (digits.length - 1);
      const num = Number(`${digits}e${decimal}`);
      return Number.isFinite(num) ? num * sign : 0;
    }
    return 0;
  };

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'ALL') return requests;
    return requests.filter((req) => req.status === statusFilter);
  }, [requests, statusFilter]);

  const openReportModal = (req: any) => {
    if (!canReport(req)) {
      addNotification(t('withdrawals.report_not_ready') || 'El reporte está disponible 24 horas después de la ejecución.', 'warning');
      return;
    }
    setReporting({
      id: req.id,
      amount: req.amount,
      currency: req.currency,
      executed_at: req.executed_at,
      reference: req.reference ?? null,
      notes: req.notes ?? null,
    });
    setReportNote('');
  };

  const closeReportModal = () => {
    if (reportingSubmit) return;
    setReporting(null);
    setReportNote('');
  };

  const submitReport = async () => {
    if (!reporting) return;
    if (!reportNote.trim()) {
      addNotification(t('withdrawals.report_note_required') || 'Agrega una nota para el reporte.', 'warning');
      return;
    }

    setReportingSubmit(true);
    try {
      const amountNumber = parseAmount(reporting.amount);
      await SupportService.createTicket({
        subject: `${t('withdrawals.report_subject') || 'Reporte de retiro'} #${reporting.id}`,
        message: reportNote.trim(),
        priority: 'high',
        source: 'WITHDRAWAL_REPORT',
        metadata: {
          withdrawalId: reporting.id,
          amount: amountNumber,
          currency: reporting.currency,
          executedAt: reporting.executed_at,
          reference: reporting.reference ?? null,
          adminNotes: reporting.notes ?? null,
          reportedAt: new Date().toISOString(),
        },
      });
      addNotification(t('withdrawals.report_success') || 'Reporte enviado a soporte.', 'success');
      closeReportModal();
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setReportingSubmit(false);
    }
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    try {
      await WithdrawalsService.sendMfaCode();
      addNotification(t('withdrawals.mfa_sent'), 'success');
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async () => {
    if (!bankAccount || !bankAccount.hasBankAccount) {
      addNotification(t('withdrawals.bank_missing'), 'warning');
      return;
    }

    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      addNotification(t('withdrawals.invalid_amount'), 'warning');
      return;
    }

    if (amountValue > available) {
      addNotification(t('withdrawals.insufficient_balance'), 'warning');
      return;
    }

    if (!mfaCode.trim()) {
      addNotification(t('withdrawals.mfa_required'), 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const res = await WithdrawalsService.create({
        amount: amountValue.toString(),
        bankAccountId: bankAccount.id,
        mfaCode: mfaCode.trim(),
        userComment: comment.trim() || undefined
      });

      addNotification(res?.message || t('withdrawals.success'), 'success');
      setAmount('');
      setComment('');
      setMfaCode('');
      await loadContext();
      await loadRequests();
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('withdrawals.title')}</h1>
          <p className={styles.subtitle}>{t('withdrawals.subtitle')}</p>
        </div>
      </header>

      <div className={styles.grid}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>{t('withdrawals.balance_title')}</div>
          <div className={styles.balanceRow}>
            <div className={styles.balanceValue}>
              {context?.wallet?.currency ?? 'MXN'} ${available.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </div>
            <div className={styles.balanceLabel}>{t('withdrawals.balance_available')}</div>
          </div>
          {context?.wallet?.updatedAt ? (
            <div className={styles.muted}>
              {t('withdrawals.balance_updated', { date: new Date(context.wallet.updatedAt).toLocaleString('es-MX') })}
            </div>
          ) : null}
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>{t('withdrawals.bank_title')}</div>
          {bankAccount?.hasBankAccount ? (
            <div className={styles.bankDetails}>
              <div>
                <div className={styles.bankName}>{bankAccount.bank?.name || 'Banco'}</div>
                <div className={styles.muted}>{bankAccount.beneficiaryName}</div>
              </div>
              <div className={styles.bankMeta}>
                <div>{bankAccount.clabeMasked}</div>
                <div className={styles.muted}>{bankAccount.currency}</div>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>{t('withdrawals.bank_missing')}</p>
              <a className={styles.linkButton} href="/perfil">
                {t('withdrawals.bank_cta')}
              </a>
            </div>
          )}
          {bankAccount?.hasBankAccount && !isVerified ? (
            <div className={styles.warning}>
              {t('withdrawals.bank_not_verified') || 'Tu cuenta bancaria está en revisión. No puedes solicitar retiro aún.'}
            </div>
          ) : null}
        </section>
      </div>

      <section className={styles.formCard}>
        <h3 className={styles.formTitle}>{t('withdrawals.form_title')}</h3>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.label}>{t('withdrawals.amount_label')}</label>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="0.01"
              placeholder={t('withdrawals.amount_placeholder')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t('withdrawals.mfa_label')}</label>
            <div className={styles.mfaRow}>
              <input
                className={styles.input}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123456"
              />
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleSendCode}
                disabled={sendingCode}
              >
                {sendingCode ? t('withdrawals.mfa_sending') : t('withdrawals.mfa_send')}
              </button>
            </div>
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>{t('withdrawals.comment_label')}</label>
            <textarea
              className={styles.textarea}
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('withdrawals.comment_placeholder')}
            />
          </div>
        </div>

        <div className={styles.formFooter}>
          <div className={styles.formHint}>{t('withdrawals.no_balance_change')}</div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? t('common.processing') : t('withdrawals.submit')}
          </button>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeaderRow}>
          <div className={styles.cardHeader}>{t('withdrawals.history_title') || 'Historial de retiros'}</div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadRequests}
            disabled={loadingRequests}
          >
            {t('common.refresh') || 'Actualizar'}
          </button>
        </div>
        <div className={styles.filters}>
          {[
            { key: 'ALL', label: t('withdrawals.filter_all') || 'Todos' },
            { key: 'PENDING', label: t('withdrawals.filter_pending') || 'Pendientes' },
            { key: 'APPROVED', label: t('withdrawals.filter_approved') || 'Aprobados' },
            { key: 'EXECUTED', label: t('withdrawals.filter_executed') || 'Ejecutados' },
            { key: 'REJECTED', label: t('withdrawals.filter_rejected') || 'Rechazados' }
          ].map((item) => (
            <button
              key={item.key}
              className={statusFilter === item.key ? styles.filterActive : styles.filterButton}
              onClick={() => setStatusFilter(item.key as any)}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>
        {loadingRequests ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : filteredRequests.length === 0 ? (
          <div className={styles.emptyState}>
            <span>{t('withdrawals.history_empty') || 'No hay solicitudes de retiro aún.'}</span>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('withdrawals.table_date') || 'Fecha'}</th>
                  <th>{t('withdrawals.table_amount') || 'Monto'}</th>
                  <th>{t('withdrawals.table_status') || 'Estado'}</th>
                  <th>{t('withdrawals.table_reference') || 'Referencia'}</th>
                  <th>{t('withdrawals.table_notes') || 'Notas'}</th>
                  <th>{t('withdrawals.table_actions') || 'Acciones'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((req) => (
                  <tr key={req.id}>
                    <td>
                      {new Date(req.requested_at).toLocaleString('es-MX', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      {parseAmount(req.amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} {req.currency}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${statusClass(req.status)}`}>
                        {statusLabel(req.status)}
                      </span>
                    </td>
                    <td>{req.reference ?? '—'}</td>
                    <td>{req.notes ?? '—'}</td>
                    <td>
                      <div className={styles.actionWrap} title={reportWaitMessage(req)}>
                        <button
                          type="button"
                          className={styles.actionButton}
                          disabled={!canReport(req)}
                          onClick={() => openReportModal(req)}
                        >
                          {t('withdrawals.report_action') || 'Reportar'}
                        </button>
                        {!canReport(req) ? (
                          <span className={styles.actionHint}>{reportWaitMessage(req)}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {reporting ? (
        <div className={styles.modalOverlay} onClick={closeReportModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeReportModal} disabled={reportingSubmit}>
              ✕
            </button>
            <h2 className={styles.modalTitle}>{t('withdrawals.report_title') || 'Reporte de retiro'}</h2>

            <div className={styles.modalBody}>
              <div className={styles.detailGrid}>
                <div>
                  <span className={styles.detailLabel}>{t('withdrawals.report_amount') || 'Monto'}</span>
                  <div className={styles.detailValue}>
                    {parseAmount(reporting.amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} {reporting.currency}
                  </div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('withdrawals.report_executed_at') || 'Ejecutado'}</span>
                  <div className={styles.detailValue}>
                    {reporting.executed_at ? new Date(reporting.executed_at).toLocaleString('es-MX') : '—'}
                  </div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('withdrawals.report_now') || 'Fecha actual'}</span>
                  <div className={styles.detailValue}>{new Date().toLocaleString('es-MX')}</div>
                </div>
              </div>

              <div className={styles.fieldWide}>
                <label className={styles.label}>{t('withdrawals.report_note_label') || 'Notas del reporte'}</label>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  placeholder={t('withdrawals.report_note_placeholder') || 'Indica si no llegó el monto o fue incorrecto.'}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.ghostButton} onClick={closeReportModal} disabled={reportingSubmit}>
                {t('common.back') || 'Volver'}
              </button>
              <button className={styles.primaryButton} onClick={submitReport} disabled={reportingSubmit}>
                {reportingSubmit ? t('common.processing') : t('withdrawals.report_submit') || 'Enviar reporte'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
